// Shared ledger hooks: .fit upload (with post-ingest navigation to the
// session cell) and Speediance actual-set sync.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { apiClient, ApiError, ApiNetworkError } from '../../lib/api';
import { useFitUploadMutation, type FitKindHint } from '../../lib/fit-upload';
import { useLedger } from '../../lib/ledger-state';
import { SyncResponseSchema, type FitIngestResponse, type SyncResponse } from '../../lib/types';

export type FitUploadResult = {
  filename: string;
  status: 'parsed' | 'duplicate' | 'failed';
  response: FitIngestResponse | null;
  error: string | null;
};

function formatUploadError(err: unknown): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach the API.";
  if (err instanceof ApiError) return err.detail ?? `Error (${err.kind})`;
  if (err instanceof Error) return err.message;
  return String(err);
}

function localYmd(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Upload one or more `.fit` files. The server attaches each to the workout
 * on its recorded date (and marks it completed); afterwards every cardio +
 * workout query refetches and the ledger selects the matched session cell.
 * `open()` pops the hidden file picker; render `<input {...inputProps} />`
 * once in the host component.
 */
export function useFitUpload(kindHint?: FitKindHint) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ledger = useLedger();
  const mut = useFitUploadMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<FitUploadResult[]>([]);

  const upload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setBusy(true);
      const settled = await Promise.allSettled(files.map((file) => mut.mutateAsync({ file, kindHint })));
      const next: FitUploadResult[] = settled.map((r, i) => {
        const filename = files[i]!.name;
        if (r.status === 'rejected') {
          return { filename, status: 'failed', response: null, error: formatUploadError(r.reason) };
        }
        const res = r.value;
        if (res.parse_status === 'failed') {
          return { filename, status: 'failed', response: res, error: res.parse_error ?? 'Parse failed.' };
        }
        return { filename, status: res.ingested ? 'parsed' : 'duplicate', response: res, error: null };
      });
      setResults(next);
      qc.invalidateQueries({ queryKey: ['cardio-sessions'] });
      qc.invalidateQueries({ queryKey: ['workouts'] });
      qc.invalidateQueries({ queryKey: ['data-health'] });
      setBusy(false);
      const landed = next.find((r) => r.status !== 'failed' && r.response);
      if (landed?.response) {
        const date = landed.response.workout_log_match?.date ?? localYmd(landed.response.recorded_at);
        ledger.select(date, 'session');
        navigate({ to: '/log' });
      }
    },
    [mut, kindHint, qc, ledger, navigate],
  );

  const open = useCallback(() => {
    if (!busy) inputRef.current?.click();
  }, [busy]);

  const inputProps = {
    ref: inputRef,
    type: 'file' as const,
    accept: '.fit',
    multiple: true,
    style: { display: 'none' } as const,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.currentTarget.files ?? []);
      e.currentTarget.value = '';
      void upload(files);
    },
  };

  return { upload, open, busy, results, inputProps };
}

/** POST /api/speediance/sync → refreshes actual sets. `since` (YYYY-MM-DD)
 *  widens the read-back window; omitted, the server reads the last 7 days. */
export function useSpeedianceSync() {
  const qc = useQueryClient();
  return useMutation<SyncResponse, Error, string | void>({
    mutationFn: (since) =>
      apiClient.post<SyncResponse>('/api/speediance/sync', since ? { since } : {}, { schema: SyncResponseSchema }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['actual-sets'] });
      qc.invalidateQueries({ queryKey: ['exercise-weights'] });
      qc.invalidateQueries({ queryKey: ['data-health'] });
    },
  });
}
